import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import EmptyState from "../components/EmptyState";
import { assetUrl } from "../lib/format";
import { ArrowUpRight, BadgeCheck } from "lucide-react";

/* ───────── Reveal-on-scroll helper ───────── */

function useInView({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, as: As = "div", style, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <As
      ref={ref}
      className={`wn-reveal${inView ? " is-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </As>
  );
}

/* ───────── Page ───────── */

export default function ArchitectsList() {
  const [architects, setArchitects] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/architects")
      .then((r) => !cancelled && setArchitects(r.data))
      .catch((e) => !cancelled && setErr(e));
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = architects
    ? architects.reduce(
        (a, x) => ({
          verified: a.verified + 1,
          designs: a.designs + (x.portfolio_count || 0),
          completed: a.completed + (x.completed_count || 0),
        }),
        { verified: 0, designs: 0, completed: 0 },
      )
    : null;

  return (
    <section className="animate-fade-in-up" data-testid="architects-page">
      {/* ─── § 01 · Hero ─── */}
      <Reveal>
        <header
          className="grid lg:grid-cols-[1fr_auto] gap-10 items-end"
          style={{ paddingBottom: 36 }}
          data-testid="architects-hero"
        >
          <div className="max-w-[860px]">
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 28,
                  height: 2,
                  background: "#B14E2F",
                }}
              />
              <span className="wn-label" style={{ color: "#8F3C23" }}>
                § 01 · Local talent · Economic opportunity
              </span>
            </div>
            <h1
              className="mt-5"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: "clamp(40px, 5.6vw, 60px)",
                fontWeight: 500,
                lineHeight: 1.04,
                letterSpacing: "-0.01em",
                color: "#1B1F4B",
                margin: 0,
              }}
            >
              Designed by the people who{" "}
              <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
                live here
              </span>
              .
            </h1>
            <p
              className="mt-5"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 19,
                lineHeight: 1.55,
                maxWidth: 680,
                margin: 0,
              }}
            >
              Every architect on the network is licensed in Indiana, carries
              current E&amp;O insurance, and has been vetted by city staff.
              Commissioning local practitioners keeps design fees circulating
              in Indianapolis. Consultations are arranged through the platform
              — no inbox spam.
            </p>
          </div>

          {/* Right-rail dateline */}
          <aside
            className="hidden lg:block"
            style={{
              borderLeft: "1px solid #D9D8E4",
              paddingLeft: 20,
              minWidth: 200,
            }}
            data-testid="architects-dateline"
          >
            <div className="wn-label" style={{ color: "#6C6B85" }}>
              Volume 01 · Network
            </div>
            <div
              className="mt-1 tabular-nums"
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                color: "#1B1F4B",
                fontSize: 15,
                letterSpacing: "0.02em",
              }}
            >
              {architects?.length ?? "—"} firms
            </div>
            <div
              className="mt-3"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              A standing roster of vetted Indianapolis practices, updated as
              applications clear.
            </div>
          </aside>
        </header>
      </Reveal>

      {/* Stats hairline row */}
      {totals && (
        <Reveal delay={80}>
          <div
            className="grid grid-cols-3"
            style={{
              borderTop: "1px solid #D9D8E4",
              borderBottom: "1px solid #D9D8E4",
              padding: "22px 0",
            }}
            data-testid="architects-stats"
          >
            {[
              { value: totals.verified, label: "Verified firms" },
              { value: totals.designs, label: "Published designs" },
              { value: totals.completed, label: "Completed projects" },
            ].map((c, i) => (
              <div
                key={c.label}
                style={{
                  paddingLeft: i === 0 ? 0 : 36,
                  borderLeft: i === 0 ? "none" : "1px solid #D9D8E4",
                }}
              >
                <div
                  className="tabular-nums"
                  style={{
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: 36,
                    color: "#1B1F4B",
                    lineHeight: 1,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {c.value}
                </div>
                <div
                  className="wn-label mt-2"
                  style={{ color: "#6C6B85" }}
                >
                  {c.label}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {/* ─── § 02 · Network roster ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 02"
          eyebrow="The network"
          title="A standing roster of vetted firms."
          dek="Each architect below has signed the platform charter. Click a profile to read their philosophy in full, see completed projects, and request a 45-minute consultation."
        />
      </Reveal>

      <div className="mt-12">
        {err ? (
          <EmptyState
            title="Could not load the architect directory."
            body="Confirm the backend is reachable, then try again."
          />
        ) : !architects ? (
          <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
            Loading architects…
          </div>
        ) : architects.length === 0 ? (
          <EmptyState
            title="No verified architects yet."
            body="Check back after onboarding completes."
          />
        ) : (
          <div
            style={{
              borderTop: "1px solid #D9D8E4",
              borderBottom: "1px solid #D9D8E4",
            }}
            data-testid="architects-grid"
          >
            {architects.map((a, i) => (
              <Reveal key={a.id} delay={i === 0 ? 0 : 80}>
                <RosterRow architect={a} index={i + 1} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {/* ─── § 03 · Join the network (action band) ─── */}
      {!err && architects && architects.length > 0 && (
        <Reveal delay={60}>
          <JoinNetworkBand />
        </Reveal>
      )}
    </section>
  );
}

/* ─────────── Roster row ─────────── */

function RosterRow({ architect, index }) {
  const photo = assetUrl(architect.firm_photo);
  const flags = architect.demographic_flags || [];
  const serviceAreas = (architect.service_areas || []).join(" · ");
  const yearsLabel = architect.years_practising
    ? `${architect.years_practising}+ yrs practising`
    : null;

  return (
    <Link
      to={`/architects/${architect.id}`}
      data-testid={`architect-row-${architect.id}`}
      className="wn-roster-row group"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(280px, 320px) minmax(0, 1fr)",
        columnGap: 40,
        alignItems: "stretch",
        padding: "32px 0",
        borderTop: index === 1 ? "none" : "1px solid #D9D8E4",
        textDecoration: "none",
        color: "inherit",
      }}
    >
      {/* Photo column */}
      <div
        className="wn-roster-photo-wrap"
        style={{
          position: "relative",
          width: "100%",
          minHeight: 220,
          overflow: "hidden",
          backgroundColor: "#EEEDF3",
          border: "1px solid #D9D8E4",
          borderRadius: 4,
        }}
      >
        <div
          aria-hidden
          className="wn-roster-photo-inner"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: photo ? `url(${photo})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "transform 520ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          }}
        />
        {/* Verified pill */}
        <span
          data-testid={`arch-verified-${architect.id}`}
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 11px 5px 9px",
            borderRadius: 999,
            backgroundColor: "rgba(27, 31, 75, 0.92)",
            color: "#FBFAFD",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 10.5,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
          }}
        >
          <BadgeCheck size={11} strokeWidth={2.4} />
          Verified
        </span>
      </div>

      {/* Editorial column */}
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 22,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            {architect.firm}
          </span>
        </div>

        <h3
          className="mt-3"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: "clamp(26px, 2.6vw, 34px)",
            lineHeight: 1.1,
            letterSpacing: "-0.005em",
            color: "#1B1F4B",
            margin: 0,
          }}
        >
          {architect.name}
        </h3>

        {architect.philosophy_quote && (
          <blockquote
            className="mt-4"
            style={{
              margin: 0,
              paddingLeft: 16,
              borderLeft: "2px solid #B14E2F",
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#272636",
              fontSize: 18,
              lineHeight: 1.5,
              maxWidth: 640,
            }}
          >
            “{architect.philosophy_quote}”
          </blockquote>
        )}

        {/* Meta strip: flags + neighborhoods */}
        <div className="mt-5 flex items-center flex-wrap" style={{ gap: 10 }}>
          {flags.map((f) => (
            <span
              key={f}
              data-testid={`arch-flag-${architect.id}-${f}`}
              style={{
                padding: "3px 9px",
                borderRadius: 999,
                border: "1px solid #D9D8E4",
                backgroundColor: "transparent",
                color: "#1B1F4B",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 10.5,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
              }}
            >
              {f}
            </span>
          ))}
          {yearsLabel && (
            <>
              {flags.length > 0 && (
                <span aria-hidden style={{ color: "#9AA0C0" }}>·</span>
              )}
              <span
                className="wn-label"
                style={{ color: "#6C6B85", letterSpacing: "0.08em" }}
              >
                {yearsLabel}
              </span>
            </>
          )}
        </div>

        <div
          className="mt-3"
          style={{
            color: "#272636",
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          <span className="wn-label" style={{ color: "#6C6B85" }}>
            Serves
          </span>{" "}
          <span style={{ marginLeft: 6 }}>
            {serviceAreas || "Indianapolis"}
          </span>
        </div>

        {/* Hairline footer · inline metrics + CTA */}
        <div
          className="mt-6 grid items-center"
          style={{
            paddingTop: 14,
            gridTemplateColumns: "minmax(0, 1fr) auto",
            columnGap: 24,
            borderTop: "1px solid #D9D8E4",
          }}
        >
          <div
            className="flex items-baseline flex-wrap"
            style={{ columnGap: 22, rowGap: 6 }}
          >
            <InlineMetric
              label="Designs"
              value={architect.portfolio_count}
            />
            <span aria-hidden style={{ color: "#D9D8E4" }}>|</span>
            <InlineMetric
              label="Completed"
              value={architect.completed_count}
            />
            <span aria-hidden style={{ color: "#D9D8E4" }}>|</span>
            <InlineMetric
              label="Specialties"
              value={architect.specialisations_count}
            />
          </div>

          <span
            className="wn-cta-arrow inline-flex items-center"
            style={{
              color: "#8F3C23",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: "0.02em",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            View profile
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function InlineMetric({ label, value }) {
  return (
    <span className="inline-flex items-baseline" style={{ gap: 6 }}>
      <span
        className="tabular-nums"
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 800,
          fontSize: 18,
          color: "#1B1F4B",
          lineHeight: 1,
          letterSpacing: "-0.005em",
        }}
      >
        {value ?? "—"}
      </span>
      <span className="wn-label" style={{ color: "#6C6B85" }}>
        {label}
      </span>
    </span>
  );
}

/* ─────────── § 03 · Join network action band ─────────── */

function JoinNetworkBand() {
  return (
    <div
      data-testid="join-network-panel"
      style={{
        marginTop: 72,
        backgroundColor: "#1B1F4B",
        color: "#FBFAFD",
        borderRadius: 4,
        padding: "44px 44px 40px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#E8927C",
          }}
        />
        <span className="wn-label" style={{ color: "#E8927C" }}>
          § 03 · Practising in Indianapolis?
        </span>
      </div>

      <div className="mt-5 grid lg:grid-cols-[1fr_auto] items-end gap-8">
        <div>
          <h3
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(28px, 3.2vw, 38px)",
              lineHeight: 1.12,
              letterSpacing: "-0.005em",
              color: "#FBFAFD",
              margin: 0,
              maxWidth: 720,
            }}
          >
            Join the verified network.
          </h3>
          <p
            className="mt-3"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "rgba(251,250,253,0.72)",
              fontSize: 17,
              lineHeight: 1.55,
              maxWidth: 620,
              margin: 0,
            }}
          >
            Licensed Indiana architects can apply to publish pre-reviewed
            designs on WHOLE Neighborhoods. Verification typically takes a few
            business days — and once you&rsquo;re in, your work is one of the
            first things City Staff sees when matching residents to designs.
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-3">
          <Link
            to="/marketplace"
            data-testid="join-network-browse"
            className="wn-cta-arrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 18px",
              borderRadius: 4,
              background: "transparent",
              color: "#FBFAFD",
              border: "1px solid rgba(251,250,253,0.45)",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.01em",
              textDecoration: "none",
            }}
          >
            Browse the marketplace
          </Link>
          <Link
            to="/register/architect"
            data-testid="join-network-cta"
            className="wn-cta-arrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 20px",
              borderRadius: 4,
              background: "#B14E2F",
              color: "#FBFAFD",
              border: "1px solid #B14E2F",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.01em",
              textDecoration: "none",
            }}
          >
            Start an application
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Section heading + divider ─────────── */

function SectionDivider() {
  return (
    <div
      aria-hidden
      style={{
        borderTop: "1px solid #D9D8E4",
        marginTop: 64,
        marginBottom: 36,
      }}
    />
  );
}

function SectionHeading({ marker, eyebrow, title, dek }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          {marker} · {eyebrow}
        </span>
      </div>
      {title && (
        <h2
          className="mt-4"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: "clamp(28px, 3.4vw, 38px)",
            lineHeight: 1.15,
            color: "#1B1F4B",
            letterSpacing: "-0.005em",
            margin: 0,
            paddingBottom: 4,
          }}
        >
          {title}
        </h2>
      )}
      {dek && (
        <p
          className="mt-3"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 16,
            lineHeight: 1.55,
            maxWidth: 760,
            margin: 0,
          }}
        >
          {dek}
        </p>
      )}
    </div>
  );
}
